import React, { useState, useEffect } from 'react';
import { 
  X, 
  Download, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  FileText,
  Loader2,
  Calendar,
  HardDrive
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { dataDownloadService, DataDownloadRequest } from '../services/dataDownloadService';
import { useAuth } from '../hooks/useAuth';
import Loading from './sub-components/loading';

interface DataDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataDownloadModal: React.FC<DataDownloadModalProps> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [downloadRequest, setDownloadRequest] = useState<DataDownloadRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);


  // Clear messages after timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Load initial status when modal opens
  useEffect(() => {
    if (isOpen && user?.id) {
      loadDownloadStatus();
    }
  }, [isOpen, user?.id]);

  const loadDownloadStatus = async () => {
    if (!user?.id) {
      setError('User not found. Please make sure you are logged in.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {      
      // console.log('Loading download status for user:', user.id);
      const status = await dataDownloadService.checkDownloadStatus(user.id);
      // console.log('Download status loaded:', status);
      setDownloadRequest(status);
    } catch (err) {
      // console.error('Error loading download status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load download status';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async () => {
    if (!user?.id) return;
    
    setIsCreatingRequest(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await dataDownloadService.createDownloadRequest(user.id, user.email);
      setSuccess(result.message);
      
      // Reload status to get the new request
      await loadDownloadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create download request');
    } finally {
      setIsCreatingRequest(false);
    }
  };

  const handleDownload = async () => {
    if (!user?.id || !downloadRequest?.downloadUrl || !downloadRequest?.fileName) return;
    
    setIsDownloading(true);
    setError(null);
    
    try {
      await dataDownloadService.downloadFile(downloadRequest.downloadUrl, downloadRequest.fileName);
      setSuccess(t('profile.data_download.download_started'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-6 h-6 text-yellow-500" />;
      case 'processing':
        return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />;
      case 'ready':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'expired':
      case 'failed':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <FileText className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'processing':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'ready':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'expired':
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <Download className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t('profile.data_download.title')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('profile.data_download.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <Loading color="indigo" />
              <p className="mt-4 text-gray-600">
                {t('profile.data_download.checking_status')}
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-red-800 mb-1">
                  {t('profile.data_download.error_title')}
                </h4>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-green-800 mb-1">
                  {t('profile.data_download.success_title')}
                </h4>
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          )}

          {/* Main Content */}
          {!loading && (
            <>
              {/* No Request State */}
              {!downloadRequest?.hasRequest && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Download className="w-8 h-8 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {t('profile.data_download.no_request_title')}
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    {t('profile.data_download.no_request_description')}
                  </p>
                  <button
                    onClick={handleCreateRequest}
                    disabled={isCreatingRequest}
                    className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCreatingRequest ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('profile.data_download.creating_request')}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        {t('profile.data_download.request_data')}
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Request Status */}
              {downloadRequest?.hasRequest && (
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className={`p-6 rounded-lg border-2 ${getStatusColor(downloadRequest.status)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start">
                        {getStatusIcon(downloadRequest.status)}
                        <div className="ml-3">
                          <h3 className="font-semibold text-lg">
                            {t(`profile.data_download.status_${downloadRequest.status || 'unknown'}`)}
                          </h3>
                          <p className="text-sm mt-1 opacity-80">
                            {downloadRequest.message || t(`profile.data_download.status_${downloadRequest.status || 'unknown'}_desc`)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Status Details */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {downloadRequest.apiStatus && (
                        <div className="flex items-center text-sm">
                          <CheckCircle className="w-4 h-4 mr-2 opacity-60" />
                          <span className="opacity-80">
                            API Status: {downloadRequest?.apiStatus}
                          </span>
                        </div>
                      )}

                      {downloadRequest.createdAt && (
                        <div className="flex items-center text-sm">
                          <Calendar className="w-4 h-4 mr-2 opacity-60" />
                          <span className="opacity-80">
                            {t('profile.data_download.requested_at')}: {new Date(downloadRequest.createdAt).toLocaleString()}
                          </span>
                        </div>
                      )}

                      {downloadRequest.fileSize && (
                        <div className="flex items-center text-sm">
                          <HardDrive className="w-4 h-4 mr-2 opacity-60" />
                          <span className="opacity-80">
                            {t('profile.data_download.file_size')}: {dataDownloadService.formatFileSize(downloadRequest.fileSize)}
                          </span>
                        </div>
                      )}

                      {downloadRequest.expiresAt && downloadRequest.status === 'ready' && (
                        <div className="flex items-center text-sm">
                          <Clock className="w-4 h-4 mr-2 opacity-60" />
                          <span className="opacity-80">
                            {t('profile.data_download.expires_in')}: {dataDownloadService.getTimeUntilExpiration(downloadRequest.expiresAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {downloadRequest.status === 'ready' && downloadRequest.downloadUrl && (
                      <button
                        onClick={handleDownload}
                        disabled={isDownloading}
                        className="flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('profile.data_download.downloading')}
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            {t('profile.data_download.download_now')}
                          </>
                        )}
                      </button>
                    )}

                    {(downloadRequest.status === 'expired' || downloadRequest.status === 'failed') && (
                      <button
                        onClick={handleCreateRequest}
                        disabled={isCreatingRequest}
                        className="flex items-center justify-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCreatingRequest ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('profile.data_download.creating_request')}
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 mr-2" />
                            {t('profile.data_download.request_new')}
                          </>
                        )}
                      </button>
                    )}

                    {!downloadRequest?.hasRequest && (
                      <button
                        onClick={loadDownloadStatus}
                        disabled={loading}
                        className="flex items-center justify-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Loader2 className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        {t('profile.data_download.refresh_status')}
                      </button>
                    )}
                  </div>

                  {/* Information */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="text-blue-500 mr-3 mt-1">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-1">
                          {t('profile.data_download.info_title')}
                        </h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• {t('profile.data_download.info_processing_time')}</li>
                          <li>• {t('profile.data_download.info_expiration')}</li>
                          <li>• {t('profile.data_download.info_includes')}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            {t('profile.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataDownloadModal;
